import {useEffect, useState, useRef} from 'react'
import Head from 'next/head'
import {usePaddleLoad} from 'src/hooks/usePaddleLoad'
import {Emoji, Input} from 'src/Common'
import {Button} from 'src/Button'
import {useUser} from 'src/hooks/useUser'
import {HStack, VStack} from '@components/Stack'
import {ProBadge} from '@components/ProBadge'
import {useWhenMounted} from 'src/hooks/useWhenMounted'
import {InnerPageHeader} from '@components/InnerPageHeader'
import {RadioTabs} from '@components/RadioTabs'
import {Panel} from '@components/Panel'
import {Text} from '@components/Text'
import {motion} from 'framer-motion'
import Link from 'next/link'
import confetti from 'canvas-confetti'
import {useFormInput} from 'src/hooks/useFormInput'
import {
  getPlayerProfile,
  getPlayerProfileFromUsername,
  listenForPlayerChange,
} from 'src/util/db'
import {toast} from 'react-toastify'
import {debounce} from 'src/utils'
import {Avatar, PlayerName} from 'src/Common'
import {Loader} from 'src/Loader'
import {sendEvent} from 'src/analytics'
import {DAILY_TARGET_COUNT_APPROX} from 'src/constants'

const ProPage = () => {
  const user = useUser()
  const isMounted = useWhenMounted()

  const hasVendorScriptLoaded = usePaddleLoad(50177)

  const [isYearlySelected, setIsYearlySelected] = useState(true)
  const [isGiftView, setIsGiftView] = useState(false)
  const [isValidatingUser, setIsValidatingUser] = useState(false)
  const giftToInput = useFormInput('')

  const [toGiftUser, setToGiftUser] = useState(null)
  const [isFormDirty, setIsFormDirty] = useState(false)

  function changeHandler(e) {
    setIsYearlySelected(e.target.value === 'yearly')
  }

  function checkout(recieverId) {
    const eventLabel = isYearlySelected ? 'yearly' : 'monthly'
    sendEvent({
      name: 'plusCheckoutStarted',
      label: eventLabel,
    })

    window.Paddle.Checkout.open({
      product: isYearlySelected ? 564882 : 561645,
      passthrough: JSON.stringify(
        isGiftView ? {userId: user.uid, recieverId} : {userId: user.uid}
      ),
      successCallback: () => {
        sendEvent({
          name: 'plusPurchased',
          label: eventLabel,
        })
        toast.info('Hold on while we upgrade you to PLUS...')
        listenForPlayerChange(isGiftView ? recieverId : user.uid, (profile) => {
          if (profile.isPro) {
            window.location.reload()
          }
        })
      },
    })
  }

  async function upgradeClickHandler() {
    if (isGiftView) {
      if (!giftToInput.value) {
        toast.error('Please enter a username or user ID')
        return
      }

      let profile = await checkIfUserExists(giftToInput.value)
      if (profile) {
        checkout(profile.id)
      } else {
        toast.error('No such player found!')
      }
    } else {
      checkout()
    }
  }

  async function giftInputChangeHandler(e, val) {
    setIsFormDirty(true)
    giftToInput.input.onChange(e, val)

    // set profile for display under the input
    let profile = await checkIfUserExists(e.target.value)
    setToGiftUser(profile)
  }

  async function checkIfUserExists(id) {
    setIsValidatingUser(true)
    // get user from username or id
    let profile
    if (id.length > 25) {
      profile = await getPlayerProfile(id, true)
    } else {
      profile = await getPlayerProfileFromUsername(id)
    }

    setIsValidatingUser(false)
    return profile
  }

  return (
    <>
      <Head>
        <title key="title">Plus | CSSBattle</title>
        <meta property="og:title" content="Plus | CSSBattle" key="ogTitle" />
      </Head>

      <InnerPageHeader
        showProBadge={true}
        title={user?.isPro ? 'You are "Plus"' : 'Upgrade to Plus'}
        subtitle="CSSBattle is free and will always be free to play. Upgrading to
                PLUS gives you the following additional sweet benefits that make
                playing on CSSBattle a little more fun and enjoyable for you."
      />

      <VStack>
        <HStack>
          <div style={{width: '25rem', paddingTop: '3rem'}}>
            <VStack>
              <ProPanelWrapper isPro={user?.isPro}>
                <LimitedTimeBanner show={isYearlySelected && !user?.isPro} />
                <Panel padding="1.5rem" fullWidth glowing>
                  {user?.isPro && !isGiftView && isMounted ? (
                    <VStack gap="2rem">
                      {' '}
                      <Text
                        size={2}
                        mono={false}
                        weight="600"
                        appearance="primary"
                        align="center"
                      >
                        <HStack gap="0.5rem">
                          <span>You are</span> <ProBadge />
                        </HStack>
                      </Text>
                      <Text
                        size={1}
                        mono={false}
                        weight="500"
                        appearance="secondary"
                        align="center"
                      >
                        You're a PLUS member and we couldn't be more thrilled.
                        Thank you for supporting us ❤️
                      </Text>
                      <HStack fullWidth>
                        <Link href="/settings?tab=membership">
                          <a
                            className="button button--primary button--full-width"
                            href="/settings?tab=membership"
                          >
                            Manage
                          </a>
                        </Link>

                        <Button
                          extraClasses="button--full-width"
                          onClick={() => {
                            setIsGiftView(!isGiftView)
                          }}
                        >
                          Gift subscription
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <VStack gap="1.5rem">
                      <RadioTabs
                        options={[
                          {label: 'Yearly (30% off)', value: 'yearly'},
                          {label: 'Monthly', value: 'monthly'},
                        ]}
                        onChange={changeHandler}
                        value={isYearlySelected ? 'yearly' : 'monthly'}
                        fullWidth={true}
                      />
                      <div className="pro-pricing">
                        {isYearlySelected ? (
                          <VStack gap="0.25rem">
                            <motion.p layoutId="pricePerMonth">
                              <span className="pro-pricing__price">$3.5</span> /
                              month
                            </motion.p>
                            <motion.p
                              initial={{y: 10, opacity: 0}}
                              animate={{y: 0, opacity: 1}}
                              className="pro-pricing__small"
                            >
                              $42 / year
                            </motion.p>
                          </VStack>
                        ) : (
                          <motion.div layoutId="pricePerMonth">
                            <span className="pro-pricing__price">$5</span> /
                            month
                          </motion.div>
                        )}
                      </div>
                      {user && isMounted ? (
                        <VStack gap="0" align="stretch" fullWidth>
                          {isGiftView ? (
                            <>
                              <GiftPlus
                                giftTo={giftToInput.value}
                                giftToOnChange={giftInputChangeHandler}
                                isGiftView={isGiftView}
                              />

                              <div style={{margin: '-1.5rem 0 2rem'}}>
                                {isFormDirty && toGiftUser ? (
                                  <div className="leaderboard__user">
                                    <Avatar
                                      small
                                      size="40"
                                      url={toGiftUser.avatar}
                                      username={toGiftUser.username}
                                      isPro={toGiftUser.isPro}
                                      userId={toGiftUser.id || toGiftUser.uid}
                                      className="leaderboard__user-avatar"
                                    />

                                    <div className="leaderboard__user-info">
                                      <PlayerName
                                        name={toGiftUser.displayName}
                                        username={toGiftUser.username}
                                        isPro={toGiftUser.isPro}
                                        userId={toGiftUser.id || toGiftUser.uid}
                                        className="leaderboard__user-name"
                                        ellipsis
                                      />
                                    </div>
                                  </div>
                                ) : null}

                                {isFormDirty && !toGiftUser ? (
                                  <HStack gap="0.2rem">
                                    {isValidatingUser ? (
                                      <Loader noMargin height="16" />
                                    ) : null}
                                    <Text
                                      mono={false}
                                      appearance="tertiary"
                                      weight="500"
                                    >
                                      {isValidatingUser
                                        ? 'Searching...'
                                        : 'User not found'}
                                    </Text>
                                  </HStack>
                                ) : null}
                              </div>
                            </>
                          ) : null}

                          <HStack fullWidth>
                            {isGiftView ? (
                              <Button
                                onClick={() => {
                                  setIsGiftView(!isGiftView)
                                }}
                              >
                                Cancel
                              </Button>
                            ) : null}

                            <Button
                              primary
                              extraClasses="button--full-width"
                              onClick={upgradeClickHandler}
                              disabled={
                                isGiftView
                                  ? !toGiftUser || !hasVendorScriptLoaded
                                  : !hasVendorScriptLoaded
                              }
                            >
                              {isGiftView
                                ? 'Gift CSSBattle PLUS'
                                : 'Upgrade to PLUS'}
                              {/* {' '}
                        {isYearlySelected ? <> $36/year </> : <> $5/month </>} */}
                            </Button>

                            {!isGiftView ? (
                              <Button
                                extraClasses="button--full-width"
                                onClick={() => {
                                  setIsGiftView(!isGiftView)
                                }}
                              >
                                Gift PLUS
                              </Button>
                            ) : null}
                          </HStack>
                        </VStack>
                      ) : (
                        <a
                          href="/login"
                          className="button button--primary button--full-width"
                        >
                          Sign in to upgrade or gift
                        </a>
                      )}
                    </VStack>
                  )}
                </Panel>
              </ProPanelWrapper>

              {user?.isPro && isMounted ? null : (
                <motion.div
                  initial={{y: -10, opacity: 0}}
                  exit={{y: -10, opacity: 0}}
                  animate={{y: 0, opacity: 1, transition: {delay: 0.2}}}
                >
                  <Text
                    size={0}
                    mono={false}
                    weight="500"
                    appearance="tertiary"
                    align="center"
                  >
                    Cancel anytime. 100% refund if cancelled within 7 days.
                    Additional taxes may apply.
                  </Text>
                </motion.div>
              )}
            </VStack>
          </div>
        </HStack>

        <motion.section
          style={{marginTop: '3rem'}}
          initial={{y: -10, opacity: 0}}
          exit={{y: -10, opacity: 0}}
          animate={{y: 0, opacity: 1, transition: {delay: 0.2}}}
        >
          <Text
            size="3"
            weight="600"
            mono={false}
            tag="h2"
            appearance="primary"
          >
            Plus membership benefits ✨
          </Text>

          <ul className="pro-features">
            <li className="pro-feature">
              <ProFeature
                title={`Unlock ${DAILY_TARGET_COUNT_APPROX}+ past daily targets`}
                image="past-targets"
                number="1"
              >
                With CSSBattle Plus, you'll be able to play{' '}
                {DAILY_TARGET_COUNT_APPROX}+ of our previous daily targets and
                see their top solutions
              </ProFeature>
            </li>
            <li className="pro-feature">
              <ProFeature
                title="Add plugins to the code editor"
                image="plugins"
                number="2"
              >
                Want to minify your code automatically before submitting? Or
                maybe convert all the <code>px</code> units to <code>in</code>{' '}
                with a press of a button? With plugins, you'll be able to do all
                that and more
              </ProFeature>
            </li>
            <li className="pro-feature">
              <ProFeature
                title="Advanced target stats"
                image="stats"
                number="3"
              >
                Get a bird's-eye view on your performance in battles with our
                advance stats. See where you're performing well and where you're
                lagging behind
              </ProFeature>
            </li>
            <li className="pro-feature">
              <ProFeature
                title="Search across your top solutions"
                image="search-top-solutions.jpg"
                number="4"
              >
                Want to reuse that highscore solution which had a particular CSS
                property, but can't remember which target it was? With Plus,
                you'll be able to search across your top battle target
                solutions.
              </ProFeature>
            </li>
            <li className="pro-feature">
              <ProFeature
                title="Favourite & share solutions"
                image="fav-submission.jpg"
                number="5"
              >
                Get ability to mark your favourite solutions and also to share
                them with anyone.
              </ProFeature>
            </li>
            <li className="pro-feature">
              <ProFeature
                title="Unlimited submissions history"
                image="submissions"
                number="6"
              >
                Be able to see all your past submissions on every target
                (otherwise limited to last 5 submissions)
              </ProFeature>
            </li>
            <li className="pro-feature">
              <ProFeature title="VSCode Editor" image="vscode" number="7">
                Feel at home with the VSCode editor? We've got you covered
              </ProFeature>
            </li>
            <li className="pro-feature">
              <ProFeature
                number="8"
                title={
                  <>
                    <span style={{verticalAlign: 'middle'}}>Plus badge</span>{' '}
                    <img
                      src="/images/pro-badge.svg"
                      width="30"
                      style={{verticalAlign: 'middle', marginLeft: '0.25rem'}}
                      alt="Pro"
                    />
                  </>
                }
                image="pro"
              >
                Get a shiny badge on your profile picture wherever it appears
                across CSSBattle <Emoji label="sunglasses" children="😎" />
              </ProFeature>
            </li>
            {/* <li className="pro-feature">
              <ProFeature title="Create public community targets" isComingSoon>
                Create your own targets for the community with your own rules.
                All the targets will be rated by the community and we'll feature
                new targets every week.
              </ProFeature>
            </li> */}
          </ul>
        </motion.section>
      </VStack>
    </>
  )
}

function ComingSoonBadge() {
  return <div className="pro-feature__coming-soon">Coming Soon</div>
}

function ProFeature({
  title,
  image = '',
  isComingSoon,
  isRemoved,
  number,
  children,
}) {
  return (
    <VStack gap="2rem" align="flex-start">
      {image ? (
        <img
          style={{borderRadius: '8px'}}
          src={
            '/images/plus-page/' +
            `${image}` +
            (image.includes('.') ? '' : '.png')
          }
          width="100%"
        />
      ) : null}

      <HStack gap="1rem" align="flex-start">
        <div className="pro-feature__number">{number}</div>

        <div style={{alignSelf: 'center'}}>
          <VStack gap="0.5em" align="flex-start">
            <h3
              className="pro-feature__title"
              style={{textDecoration: isRemoved ? 'line-through' : null}}
            >
              {title} {isComingSoon && <ComingSoonBadge />}
            </h3>
            <p
              className="pro-feature__desc"
              style={{textDecoration: isRemoved ? 'line-through' : null}}
            >
              {children}
            </p>
          </VStack>
        </div>
      </HStack>
    </VStack>
  )
}

const ProPanelWrapper = ({isPro, children}) => {
  const ref = useRef(null)
  useEffect(() => {
    if (isPro && ref.current) {
      const bounds = ref.current.getBoundingClientRect()
      const x = bounds.left + bounds.width / 2

      confetti({
        particleCount: 100,
        spread: 70,
        origin: {y: 0.6, x: x / window.innerWidth},
      })
    }
  }, [isPro])

  return (
    <motion.div
      ref={ref}
      className="pro-pricing-panel"
      initial={{opacity: 0}}
      exit={{opacity: 0}}
      animate={{opacity: 1}}
    >
      {children}
    </motion.div>
  )
}

const GiftPlus = ({giftTo, giftToOnChange, isGiftView}) => {
  return (
    <>
      {isGiftView ? (
        <motion.div
          initial={{y: -10, opacity: 0}}
          exit={{y: -10, opacity: 0}}
          animate={{y: 0, opacity: 1}}
        >
          <Input
            label="User ID / username"
            autoComplete="off"
            value={giftTo}
            onChange={giftToOnChange}
            fullWidth
          />
        </motion.div>
      ) : null}
    </>
  )
}

const LimitedTimeBanner = ({show}) => {
  return (
    <div className="pricing-limited-time-banner" data-show={show}>
      Limited time 30% OFF!
    </div>
  )
}

export default ProPage
